import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@werkstatt/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@werkstatt/ui';
import { Textarea } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import type { Vehicle, VehicleCreate, VehicleUpdate } from '../../types/vehicle';

interface VehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle: Vehicle | null;
  onSuccess: () => void;
}

const defaultForm = (): Partial<VehicleCreate> => ({
  internal_number: '',
  license_plate: '',
  manufacturer: '',
  model: '',
  variant: '',
  category: 'kleinwagen',
  fuel_type: 'benzin',
  transmission: 'manuell',
  seats: 5,
  doors: 4,
  condition: 'gut',
  current_mileage: 0,
  price_per_day_cents: 2900,
  price_per_km_cents: 0,
  included_km_per_day: 200,
  deposit_cents: 50000,
  status: 'available',
  is_active: true,
});

function centsToEuro(cents: number | undefined): string {
  return ((cents ?? 0) / 100).toFixed(2);
}

function euroToCents(val: string): number {
  return Math.round(parseFloat(val.replace(',', '.')) * 100) || 0;
}

export function VehicleDialog({
  open,
  onOpenChange,
  vehicle,
  onSuccess,
}: VehicleDialogProps) {
  const { toast } = useToast();
  const isEdit = !!vehicle;
  const [formData, setFormData] = useState<Partial<VehicleCreate>>(defaultForm());

  const { data: categories } = useQuery({
    queryKey: ['vehicle-categories'],
    queryFn: async () => {
      const response = await api.get<{ key: string; label: string; is_active: boolean }[]>(
        '/categories'
      );
      return response.data;
    },
    enabled: open,
  });
  const categoryOptions = (categories ?? []).filter(
    (c) => c.is_active || c.key === vehicle?.category
  );

  useEffect(() => {
    if (!open) return;
    if (vehicle) {
      setFormData({
        internal_number: vehicle.internal_number,
        license_plate: vehicle.license_plate ?? '',
        vin: vehicle.vin ?? '',
        manufacturer: vehicle.manufacturer,
        model: vehicle.model,
        variant: vehicle.variant ?? '',
        category: vehicle.category,
        fuel_type: vehicle.fuel_type ?? 'benzin',
        transmission: vehicle.transmission ?? 'manuell',
        power_kw: vehicle.power_kw ?? undefined,
        power_hp: vehicle.power_hp ?? undefined,
        seats: vehicle.seats ?? 5,
        doors: vehicle.doors ?? 4,
        color: vehicle.color ?? '',
        condition: vehicle.condition,
        current_mileage: vehicle.current_mileage,
        tire_type: vehicle.tire_type ?? undefined,
        tuv_expires_at: vehicle.tuv_expires_at ?? '',
        next_service_at: vehicle.next_service_at ?? '',
        next_service_km: vehicle.next_service_km ?? undefined,
        price_per_day_cents: vehicle.price_per_day_cents,
        price_per_week_cents: vehicle.price_per_week_cents ?? undefined,
        price_per_month_cents: vehicle.price_per_month_cents ?? undefined,
        price_per_km_cents: vehicle.price_per_km_cents,
        included_km_per_day: vehicle.included_km_per_day,
        deposit_cents: vehicle.deposit_cents,
        status: vehicle.status,
        is_active: vehicle.is_active,
        notes: vehicle.notes ?? '',
      });
    } else {
      setFormData(defaultForm());
    }
  }, [vehicle, open]);

  const set = (patch: Partial<VehicleCreate>) => setFormData((prev) => ({ ...prev, ...patch }));

  const createMutation = useMutation({
    mutationFn: (data: VehicleCreate) => api.post('/vehicles', data).then((r) => r.data),
    onSuccess: () => {
      toast({ title: 'Fahrzeug erstellt' });
      onSuccess();
    },
    onError: (error) => {
      toast({ title: 'Fehler', description: (isAxiosError(error) ? error.response?.data?.detail : undefined) ?? 'Erstellen fehlgeschlagen', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: VehicleUpdate) => api.put(`/vehicles/${vehicle!.id}`, data).then((r) => r.data),
    onSuccess: () => {
      toast({ title: 'Fahrzeug aktualisiert' });
      onSuccess();
    },
    onError: (error) => {
      toast({ title: 'Fehler', description: (isAxiosError(error) ? error.response?.data?.detail : undefined) ?? 'Speichern fehlgeschlagen', variant: 'destructive' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) updateMutation.mutate(formData as VehicleUpdate);
    else createMutation.mutate(formData as VehicleCreate);
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="fahrzeug" className="w-full">
            <TabsList className="w-full mb-4">
              <TabsTrigger value="fahrzeug" className="flex-1">Fahrzeug</TabsTrigger>
              <TabsTrigger value="technik" className="flex-1">Technik & Fristen</TabsTrigger>
              <TabsTrigger value="preise" className="flex-1">Preise</TabsTrigger>
            </TabsList>

            {/* ── Tab 1: Fahrzeug ── */}
            <TabsContent value="fahrzeug" className="space-y-3 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="internal_number">Fahrzeugnummer *</Label>
                  <Input
                    id="internal_number"
                    required
                    placeholder="z.B. LW-001"
                    value={formData.internal_number}
                    onChange={(e) => set({ internal_number: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="license_plate">Kennzeichen</Label>
                  <Input
                    id="license_plate"
                    placeholder="z.B. MYK-AB 123"
                    value={formData.license_plate}
                    onChange={(e) => set({ license_plate: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="manufacturer">Hersteller *</Label>
                  <Input
                    id="manufacturer"
                    required
                    placeholder="z.B. VW"
                    value={formData.manufacturer}
                    onChange={(e) => set({ manufacturer: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="model">Modell *</Label>
                  <Input
                    id="model"
                    required
                    placeholder="z.B. Golf"
                    value={formData.model}
                    onChange={(e) => set({ model: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="variant">Variante</Label>
                  <Input
                    id="variant"
                    placeholder="z.B. 1.5 TSI"
                    value={formData.variant}
                    onChange={(e) => set({ variant: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Kategorie *</Label>
                  <Select value={formData.category} onValueChange={(v) => set({ category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map((c) => (
                        <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Status *</Label>
                  <Select value={formData.status} onValueChange={(v: string) => set({ status: v as VehicleCreate['status'] })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Verfügbar</SelectItem>
                      <SelectItem value="rented">Vermietet</SelectItem>
                      <SelectItem value="maintenance">Wartung</SelectItem>
                      <SelectItem value="inactive">Inaktiv</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Zustand</Label>
                  <Select value={formData.condition} onValueChange={(v) => set({ condition: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neuwertig">Neuwertig</SelectItem>
                      <SelectItem value="gut">Gut</SelectItem>
                      <SelectItem value="mittel">Mittel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="current_mileage">KM-Stand</Label>
                  <Input
                    id="current_mileage"
                    type="number"
                    min="0"
                    value={formData.current_mileage}
                    onChange={(e) => set({ current_mileage: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="notes">Notizen</Label>
                <Textarea
                  id="notes"
                  rows={2}
                  placeholder="Optionale Notizen..."
                  value={formData.notes ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => set({ notes: e.target.value })}
                />
              </div>
            </TabsContent>

            {/* ── Tab 2: Technik & Fristen ── */}
            <TabsContent value="technik" className="space-y-3 mt-0">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Kraftstoff</Label>
                  <Select value={formData.fuel_type} onValueChange={(v) => set({ fuel_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="benzin">Benzin</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="elektro">Elektro</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Getriebe</Label>
                  <Select value={formData.transmission} onValueChange={(v) => set({ transmission: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manuell">Manuell</SelectItem>
                      <SelectItem value="automatik">Automatik</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="color">Farbe</Label>
                  <Input
                    id="color"
                    placeholder="z.B. Schwarz"
                    value={formData.color}
                    onChange={(e) => set({ color: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="seats">Sitze</Label>
                  <Input
                    id="seats"
                    type="number"
                    min="1"
                    value={formData.seats}
                    onChange={(e) => set({ seats: parseInt(e.target.value) || 5 })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="doors">Türen</Label>
                  <Input
                    id="doors"
                    type="number"
                    min="2"
                    value={formData.doors}
                    onChange={(e) => set({ doors: parseInt(e.target.value) || 4 })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="power_kw">kW</Label>
                  <Input
                    id="power_kw"
                    type="number"
                    min="0"
                    placeholder="—"
                    value={formData.power_kw ?? ''}
                    onChange={(e) => set({ power_kw: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="power_hp">PS</Label>
                  <Input
                    id="power_hp"
                    type="number"
                    min="0"
                    placeholder="—"
                    value={formData.power_hp ?? ''}
                    onChange={(e) => set({ power_hp: e.target.value ? parseInt(e.target.value) : undefined })}
                  />
                </div>
              </div>

              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Fristen & Reifen</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="tuv_expires_at">TÜV fällig</Label>
                    <Input
                      id="tuv_expires_at"
                      type="date"
                      value={formData.tuv_expires_at ?? ''}
                      onChange={(e) => set({ tuv_expires_at: e.target.value || undefined })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="next_service_at">Nächste Wartung (Datum)</Label>
                    <Input
                      id="next_service_at"
                      type="date"
                      value={formData.next_service_at ?? ''}
                      onChange={(e) => set({ next_service_at: e.target.value || undefined })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label htmlFor="next_service_km">Nächste Wartung (km)</Label>
                    <Input
                      id="next_service_km"
                      type="number"
                      min="0"
                      placeholder="z.B. 50000"
                      value={formData.next_service_km ?? ''}
                      onChange={(e) => set({ next_service_km: e.target.value ? parseInt(e.target.value) : undefined })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Bereifung</Label>
                    <Select
                      value={formData.tire_type ?? 'none'}
                      onValueChange={(v) => set({ tire_type: v === 'none' ? undefined : v as VehicleCreate['tire_type'] })}
                    >
                      <SelectTrigger><SelectValue placeholder="Wählen..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— keine Angabe —</SelectItem>
                        <SelectItem value="sommer">Sommerreifen</SelectItem>
                        <SelectItem value="winter">Winterreifen</SelectItem>
                        <SelectItem value="allwetter">Allwetterreifen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ── Tab 3: Preise ── */}
            <TabsContent value="preise" className="space-y-3 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="price_per_day_cents">Tagespreis (€) *</Label>
                  <Input
                    id="price_per_day_cents"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    placeholder="29,00"
                    value={centsToEuro(formData.price_per_day_cents)}
                    onChange={(e) => set({ price_per_day_cents: euroToCents(e.target.value) })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="deposit_cents">Kaution (€)</Label>
                  <Input
                    id="deposit_cents"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="500,00"
                    value={centsToEuro(formData.deposit_cents)}
                    onChange={(e) => set({ deposit_cents: euroToCents(e.target.value) })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="price_per_week_cents">Wochenpreis (€)</Label>
                  <Input
                    id="price_per_week_cents"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="—"
                    value={formData.price_per_week_cents != null ? centsToEuro(formData.price_per_week_cents) : ''}
                    onChange={(e) => set({ price_per_week_cents: e.target.value ? euroToCents(e.target.value) : undefined })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="price_per_month_cents">Monatspreis (€)</Label>
                  <Input
                    id="price_per_month_cents"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="—"
                    value={formData.price_per_month_cents != null ? centsToEuro(formData.price_per_month_cents) : ''}
                    onChange={(e) => set({ price_per_month_cents: e.target.value ? euroToCents(e.target.value) : undefined })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="included_km_per_day">Freikilometer / Tag</Label>
                  <Input
                    id="included_km_per_day"
                    type="number"
                    min="0"
                    value={formData.included_km_per_day}
                    onChange={(e) => set({ included_km_per_day: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="price_per_km_cents">Preis / km (€)</Label>
                  <Input
                    id="price_per_km_cents"
                    type="number"
                    min="0"
                    step="0.01"
                    value={centsToEuro(formData.price_per_km_cents)}
                    onChange={(e) => set({ price_per_km_cents: euroToCents(e.target.value) })}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3 pt-4 mt-2 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Wird gespeichert...' : isEdit ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
